// Copyright (c) 2026. Hardfin, Inc. All rights reserved.

/**
 * One row in the drift-resolution popover: shows the field label,
 * Hardfin vs Salesforce values, a Pull button (copy SFDC into
 * Hardfin) and a Dismiss button (accept the divergence).
 *
 * The popover stays open after either action — both mutations
 * invalidate the discrepancies query, which removes this card from
 * the list on the next render. The popover's empty state handles
 * the "everything is resolved" case without closing.
 */
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import hardfinLogo from '@/assets/logo.png';
import { IntegrationIcon } from '@/components/integration-icon';
import { Link as RouterLink } from '@/components/link';
import { Close, KeyboardBackspace } from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import { useSupportError } from '@/hooks/use-support-error';
import {
  FieldDiscrepancy,
  SFDCDriftEntityType,
  SFDCDriftResolutionResponse,
} from '@/sfdc/api/types';
import { useDismissSFDCDriftField } from '@/sfdc/hooks/use-dismiss-sfdc-drift-field';
import { usePullSFDCDriftField } from '@/sfdc/hooks/use-pull-sfdc-drift-field';
import { SparsePerson } from '@/sparse/api/types';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { DateTime } from 'luxon';
import { ReactNode, useEffect, useRef } from 'react';

interface Props {
  entityType: SFDCDriftEntityType;
  entityKey: string;
  /** Human-readable entity name for the pull-success toast. */
  entityDisplayName?: string;
  field: FieldDiscrepancy;
  /**
   * Dismissal audit payload — populated only for cards rendered in
   * the popover's "Dismissed records" section. When present the card
   * hides the Dismiss link (the user already accepted the divergence)
   * and renders a "Dismissed {when} by {who}" footer. Pull stays
   * available — pulling clears the dismissal audit (see
   * MarkFieldPulled) and removes the row from the dismissed list on
   * the next refetch.
   */
  dismissal?: {
    dismissedAt: DateTime | null;
    dismissedBy: SparsePerson | null;
  };
  /**
   * Fires after either mutation succeeds, AFTER the hook-level
   * invalidations. The caller wires in any additional query
   * invalidations (e.g., the parent location list).
   */
  onResolved?: () => void;
}

const SIDE_ICON_SIZE = 20;

// All-zeros UUID returned by the BE when a recorded dismissed_by user
// can't be resolved.
const UNKNOWN_USER_ID = '00000000-0000-0000-0000-000000000000';

// Per-entity toast metadata: label for the sentence + route for the
// inline link. Add a case when a new DriftEntityType lands.
function toastTargetFor(
  entityType: SFDCDriftEntityType,
  entityKey: string
): {
  label: string;
  to: string;
  params: Record<string, string>;
} {
  switch (entityType) {
    case 'location':
      return {
        label: 'Location',
        to: '/locations/$locationKey',
        params: { locationKey: entityKey },
      };
    case 'company':
      return {
        label: 'Company',
        to: '/companies/$companyKey',
        params: { companyKey: entityKey },
      };
    case 'project':
      return {
        label: 'Project',
        to: '/projects/$projectKey',
        params: { projectKey: entityKey },
      };
  }
}

export function DriftFieldCard(props: Props) {
  const pull = usePullSFDCDriftField(props.entityType, props.entityKey);
  const dismiss = useDismissSFDCDriftField(props.entityType, props.entityKey);
  const { showSupportError } = useSupportError();
  const { showToast } = useToast();

  // Per-call onSuccess fires AFTER the hook-level invalidations (per
  // TanStack Query's documented ordering), so onResolved sees a cache
  // where the diff + entity-detail queries have already been
  // invalidated. Pull also fires a toast since listing pages may push
  // the row off-screen when its name/address changes.
  const showPullToast = (response: SFDCDriftResolutionResponse) => {
    const displayName = response.data.displayName || props.entityDisplayName;
    if (!displayName) {
      return;
    }
    const target = toastTargetFor(props.entityType, props.entityKey);
    showToast({
      severity: 'success',
      element: (
        <Box component="span">
          {target.label}{' '}
          <RouterLink
            to={target.to}
            params={target.params}
            color="inherit"
            underline="always"
          >
            {displayName}
          </RouterLink>{' '}
          updated
        </Box>
      ),
    });
  };
  const onPull = () => {
    if (pull.isPending || dismiss.isPending) {
      return;
    }
    // Reset so a prior dismiss failure doesn't mask this pull's outcome.
    dismiss.reset();
    pull.mutate(
      { fieldName: props.field.fieldName },
      {
        onSuccess: (response) => {
          showPullToast(response);
          props.onResolved?.();
        },
      }
    );
  };
  const onDismiss = () => {
    if (pull.isPending || dismiss.isPending) {
      return;
    }
    pull.reset();
    dismiss.mutate(
      { fieldName: props.field.fieldName },
      { onSuccess: () => props.onResolved?.() }
    );
  };

  // Inline-display when the BE returned a known error code: a
  // ViolationMap entry (5000-5100) OR any ErrorCode with an
  // ERROR_MESSAGES entry (e.g., the sfdc_* drift codes). Everything
  // else rolls up to Sentry + the SupportSnackbar.
  const mutationError = pull.error ?? dismiss.error;
  const apiError = mutationError ? handleAPIError(mutationError) : null;
  const curatedMessage = apiError
    ? getUserFacingErrorMessage(apiError)
    : undefined;
  const inlineMessage =
    curatedMessage !== undefined &&
    curatedMessage !== SUPPORT_FALLBACK_ERROR_MESSAGE
      ? curatedMessage
      : undefined;
  const dismissError = () => {
    pull.reset();
    dismiss.reset();
  };

  // Track which error reference we've already reported so the effect
  // doesn't double-fire on parent rerender.
  const reportedErrorRef = useRef<unknown>(null);
  useEffect(() => {
    if (!mutationError || inlineMessage) {
      return;
    }
    if (reportedErrorRef.current === mutationError) {
      return;
    }
    reportedErrorRef.current = mutationError;
    if (apiError && !apiError.sentToSentry) {
      captureException(mutationError);
    }
    showSupportError(
      'Something went wrong updating Salesforce sync. Our team has been notified.'
    );
  }, [mutationError, inlineMessage, apiError, showSupportError]);

  const isDismissed = props.dismissal !== undefined;
  const isMutating = pull.isPending || dismiss.isPending;

  // Reference fields whose SFDC-side cached Account name hasn't
  // propagated yet: render "Update available" instead of the opaque
  // SFDC id and block Pull (the resolve path would fail with
  // ErrAccountReferenceNotCached anyway). Should be near-impossible in
  // practice — Account CDC primes the cache before any drift can
  // surface — but the guardrail matters when timing slips.
  //
  // The `sfdcValue !== null` guard distinguishes "cache pending" from
  // "SFDC cleared the reference"; the latter case falls through to the
  // standard `(empty)` placeholder and a normal failure on Pull.
  const isSyncPending =
    props.field.kind === 'reference' &&
    props.field.sfdcValue !== null &&
    props.field.sfdcDisplay === null;
  const sfdcSideDisplay = isSyncPending
    ? 'Update available'
    : props.field.sfdcDisplay;

  return (
    <Paper
      variant="outlined"
      sx={{ padding: 2.5, borderRadius: 1.5, position: 'relative' }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="subtitle1">{props.field.label}</Typography>
          {!isDismissed && (
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="hover"
              onClick={onDismiss}
              aria-disabled={isMutating}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                ...(isMutating && {
                  color: 'text.disabled',
                  pointerEvents: 'none',
                }),
              }}
            >
              <Close fontSize="small" />
              Dismiss
            </Link>
          )}
        </Stack>

        {inlineMessage && (
          <Alert severity="error" onClose={dismissError} variant="outlined">
            {inlineMessage}
          </Alert>
        )}

        <Stack direction="row" alignItems="flex-start" spacing={3}>
          <ValueColumn
            label="Hardfin"
            icon={
              <Box
                aria-hidden
                sx={{
                  width: SIDE_ICON_SIZE,
                  height: SIDE_ICON_SIZE,
                  backgroundImage: `url(${hardfinLogo})`,
                  backgroundSize: 'auto 100%',
                  backgroundPosition: 'left center',
                  backgroundRepeat: 'no-repeat',
                }}
              />
            }
            value={props.field.hardfinValue}
            display={props.field.hardfinDisplay}
          />
          <Tooltip
            arrow
            title={isSyncPending ? 'Sync pending' : ''}
            disableHoverListener={!isSyncPending}
            disableFocusListener={!isSyncPending}
            disableTouchListener={!isSyncPending}
          >
            <Box component="span">
              <LoadingButton
                size="small"
                startIcon={<KeyboardBackspace fontSize="small" />}
                loading={pull.isPending}
                disabled={dismiss.isPending || isSyncPending}
                onClick={onPull}
              >
                Pull
              </LoadingButton>
            </Box>
          </Tooltip>
          <ValueColumn
            label="Salesforce"
            icon={
              <IntegrationIcon integrationId="SFDC" width={SIDE_ICON_SIZE} />
            }
            value={props.field.sfdcValue}
            display={sfdcSideDisplay}
          />
        </Stack>

        {props.dismissal && (
          <Typography variant="caption" color="text.secondary">
            Dismissed
            {props.dismissal.dismissedAt &&
              ` ${props.dismissal.dismissedAt.toLocaleString(DateTime.DATETIME_MED)}`}
            {' by '}
            {formatDismisser(props.dismissal.dismissedBy)}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

function formatDismisser(user: SparsePerson | null): string {
  if (!user || user.id === UNKNOWN_USER_ID) {
    return 'an unknown user';
  }
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || 'an unknown user';
}

function ValueColumn(props: {
  label: string;
  icon: ReactNode;
  value: string | null;
  /**
   * Friendly name for reference-type fields (e.g., Customer or
   * Location). When set, renders in place of the raw `value` — the
   * raw SFDC Account id is operator-hostile and gets hidden entirely
   * when a name is available.
   */
  display: string | null;
}) {
  const primary = props.display ?? props.value;
  return (
    <Stack flex={1} minWidth={0} spacing={0.5}>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        {props.icon}
        <Typography variant="subtitle2">{props.label}</Typography>
      </Stack>
      <Typography
        variant="body2"
        sx={{
          overflowWrap: 'anywhere',
        }}
      >
        {primary || (
          <Typography
            component="span"
            variant="body2"
            color="text.disabled"
            fontStyle="italic"
          >
            (empty)
          </Typography>
        )}
      </Typography>
    </Stack>
  );
}
